/**
 * Shared branded PDF writer (jsPDF, A4 portrait).
 * One place for the Rapid Fusion report look — header band, section titles,
 * key-value rows, tables, stat boxes, wrapped text and the disclaimer footer.
 * Used by the CTE Build Plan, CTE Cycle Simulation and Design Check exports.
 */

import { jsPDF } from 'jspdf';

export const BRAND_COLOR = [167, 78, 158];
export const TEXT_COLOR = [51, 51, 51];
export const MUTED_COLOR = [120, 120, 120];
export const WHITE = [255, 255, 255];
export const LIGHT_BG = [248, 249, 250];
export const BORDER_COLOR = [200, 200, 200];
export const GREEN = [16, 185, 129];
export const AMBER = [245, 158, 11];
export const RED = [239, 68, 68];

export const PAGE_W = 210;
export const PAGE_H = 297;
export const MARGIN = 15;
export const CONTENT_W = PAGE_W - MARGIN * 2;

export function createDoc() {
  return new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
}

export function checkPage(doc, y, needed = 30) {
  if (y + needed > PAGE_H - MARGIN) {
    doc.addPage();
    return MARGIN;
  }
  return y;
}

export function drawHeader(doc, title) {
  doc.setFillColor(...BRAND_COLOR);
  doc.rect(0, 0, PAGE_W, 32, 'F');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.setTextColor(...WHITE);
  doc.text(title, MARGIN, 18);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text('Rapid Fusion Ltd', PAGE_W - MARGIN, 12, { align: 'right' });
  doc.text(new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }), PAGE_W - MARGIN, 18, { align: 'right' });

  return 42;
}

export function drawSectionTitle(doc, y, title) {
  y = checkPage(doc, y, 20);
  doc.setFillColor(...BRAND_COLOR);
  doc.rect(MARGIN, y, CONTENT_W, 0.5, 'F');
  y += 6;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(...BRAND_COLOR);
  doc.text(title, MARGIN, y);
  y += 8;
  doc.setTextColor(...TEXT_COLOR);
  return y;
}

export function drawKeyValue(doc, y, label, value, x = MARGIN, labelW = 50) {
  y = checkPage(doc, y, 8);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...MUTED_COLOR);
  doc.text(label, x, y);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...TEXT_COLOR);
  doc.text(String(value), x + labelW, y);
  return y + 6;
}

export function drawTable(doc, y, headers, rows) {
  const colW = CONTENT_W / headers.length;

  y = checkPage(doc, y, 12 + rows.length * 8);

  doc.setFillColor(...BRAND_COLOR);
  doc.setTextColor(...WHITE);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.rect(MARGIN, y, CONTENT_W, 8, 'F');
  headers.forEach((h, i) => {
    doc.text(h, MARGIN + i * colW + 3, y + 5.5);
  });
  y += 8;

  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...TEXT_COLOR);
  doc.setFontSize(8);

  rows.forEach((row, ri) => {
    y = checkPage(doc, y, 8);
    if (ri % 2 === 0) {
      doc.setFillColor(...LIGHT_BG);
      doc.rect(MARGIN, y, CONTENT_W, 8, 'F');
    }
    doc.setDrawColor(...BORDER_COLOR);
    doc.line(MARGIN, y + 8, MARGIN + CONTENT_W, y + 8);
    row.forEach((cell, i) => {
      doc.text(String(cell), MARGIN + i * colW + 3, y + 5.5);
    });
    y += 8;
  });

  return y + 4;
}

export function drawStatBox(doc, x, y, w, label, value, unit) {
  doc.setFillColor(...LIGHT_BG);
  doc.setDrawColor(...BORDER_COLOR);
  doc.roundedRect(x, y, w, 22, 2, 2, 'FD');

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.5);
  doc.setTextColor(...MUTED_COLOR);
  doc.text(label.toUpperCase(), x + w / 2, y + 5, { align: 'center' });

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(...BRAND_COLOR);
  doc.text(String(value), x + w / 2, y + 14, { align: 'center' });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.5);
  doc.setTextColor(...MUTED_COLOR);
  doc.text(unit, x + w / 2, y + 19, { align: 'center' });
}

export function drawWrappedText(doc, y, text, fontSize = 9) {
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(fontSize);
  doc.setTextColor(...TEXT_COLOR);

  const lines = doc.splitTextToSize(text, CONTENT_W);
  const lineH = fontSize * 0.45;

  for (const line of lines) {
    y = checkPage(doc, y, lineH + 2);
    doc.text(line, MARGIN, y);
    y += lineH;
  }
  return y + 2;
}

export function stripMarkdown(md) {
  if (!md) return '';
  return md
    .replace(/#{1,4}\s*/g, '')
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/\*(.+?)\*/g, '$1')
    .replace(/^[-*]\s+/gm, '  - ')
    .replace(/^\d+\.\s+/gm, '  ')
    .trim();
}

const DISCLAIMER = 'Rapid Fusion accepts no responsibility for the accuracy of results produced by this tool. All outputs are provided as a computational aid only and do not constitute engineering advice. A qualified engineer must review and verify all results and compensated geometry before manufacturing.';

/** Brand rule + disclaimer + "Rapid Fusion Ltd | <reportName>" + timestamp. */
export function drawDisclaimerFooter(doc, y, reportName) {
  y = checkPage(doc, y, 30);
  y += 6;
  doc.setDrawColor(...BRAND_COLOR);
  doc.line(MARGIN, y, MARGIN + CONTENT_W, y);
  y += 6;
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(7);
  doc.setTextColor(...MUTED_COLOR);
  const discLines = doc.splitTextToSize(DISCLAIMER, CONTENT_W);
  for (const line of discLines) {
    y = checkPage(doc, y, 5);
    doc.text(line, MARGIN, y);
    y += 3.5;
  }

  y += 4;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.text(`Rapid Fusion Ltd | ${reportName}`, PAGE_W / 2, y, { align: 'center' });
  y += 3.5;
  doc.text(`Generated: ${new Date().toLocaleString('en-GB')}`, PAGE_W / 2, y, { align: 'center' });
  return y;
}
